import { Attesa, Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Modale, Tabella } from '../../componenti/base.tsx'
import { useImportazione } from '../../componenti/importazione.tsx'

/**
 * Albo dei mediatori > «Importa da Excel» (richiesta di Davide del 30/10/2026), per chi gestisce l'albo.
 *
 * Tre passi, come «Importa da foglio» degli utenti: il tracciato da scaricare e compilare (con le
 * tendine sulle voci a catalogo), l'anteprima riga per riga e l'esito. Entrano solo i mediatori nuovi:
 * quelli che ci sono gia' (stesso numero dell'albo, stessa email, stesso nome e cognome) si saltano e
 * non si aggiornano. Le righe con errori partono escluse; se se ne include una, l'importazione non parte.
 * Il flusso e' comune con l'importazione delle schede anonimizzate (componenti/importazione.tsx).
 */

type Riga = {
  numero: number
  valori: Record<string, string>
  stato: 'nuovo' | 'doppione' | 'errore'
  errori: string[]
  avvisi: string[]
  doppione: { id: string; nome: string; cognome: string; motivo: string } | null
  lettura: {
    interno: boolean; lingue: string[]; sedi: string[]; tutte_le_sedi: boolean; comune_lavoro: string | null
    comune: string | null; paese: string | null
  }
}
type Esito = { creati: { numero: number; nome: string; cognome: string }[]; doppioni: number[] }

export function ImportaMediatori({ chiudi, importati }: {
  chiudi: () => void
  /** Dopo la conferma: l'elenco si ricarica. La finestra resta aperta sull'esito. */
  importati: () => void
}) {
  const {
    fase, errore, inCorso, nomeFile, anteprima, escluse, ricontrollo, esito, ingresso,
    leggi, cambiaInclusione, importa, ricomincia,
  } = useImportazione<Riga, Esito>({
    indirizzo: '/mediatori/importazione',
    // Le righe con errori e i doppioni partono esclusi: si importa solo cio' che e' pronto.
    esclusaAllInizio: (r) => r.stato !== 'nuovo',
    importati,
  })

  const righe = anteprima?.righe ?? []
  const incluse = righe.filter((r) => !escluse.has(r.numero))
  const daCorreggere = incluse.filter((r) => r.stato === 'errore')
  const pronte = incluse.filter((r) => r.stato === 'nuovo').length
  const doppioni = righe.filter((r) => r.stato === 'doppione').length

  const titolo = fase === 'foglio' ? 'Importa da Excel · passo 1 di 3'
    : fase === 'anteprima' ? 'Importa da Excel · passo 2 di 3, anteprima' : 'Importa da Excel · passo 3 di 3, fatto'

  return (
    <Modale titolo={titolo} larga chiudi={chiudi}
            azioni={fase === 'foglio'
              ? <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
              : fase === 'anteprima'
                ? <>
                  <Bottone variante="discreto" onClick={ricomincia}>
                    Carica un altro foglio
                  </Bottone>
                  <Bottone variante="primario" inCorso={inCorso}
                           disabled={ricontrollo || pronte === 0 || daCorreggere.length > 0}
                           onClick={() => void importa()}>
                    {pronte === 1 ? 'Importa 1 mediatore' : `Importa ${pronte} mediatori`}
                  </Bottone>
                </>
                : <Bottone variante="primario" onClick={chiudi}>Fatto</Bottone>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {fase === 'foglio' && (
        <>
          <p className="nota-modulo">
            Scarica il tracciato, compilalo in Excel (una riga per mediatore) e caricalo. Prima di inserire
            qualcosa il gestionale ti mostra l’anteprima.
          </p>
          <ul className="operatori-conseguenze">
            <li><strong>Nome</strong> e <strong>Cognome</strong> sono obbligatori; il resto si può lasciare vuoto.</li>
            <li>
              Lingue, sedi, rapporto, paese, contratto e le colonne «Sì/No» hanno la <strong>tendina</strong>:
              scegli il valore dall’elenco. Fino a tre lingue e tre sedi; le altre si aggiungono poi da «Modifica».
            </li>
            <li>
              Il <strong>comune in cui lavora</strong> va indicato: se manca, il mediatore entra lo stesso, ma resta da
              completare da «Modifica». I comuni, quello in cui lavora e quello di residenza, si scrivono per nome,
              con la provincia se ce ne sono due: «Castro (LE)».
            </li>
            <li>
              Entrano solo i mediatori <strong>nuovi</strong>: chi c’è già (stesso numero dell’albo, stessa email,
              oppure stesso nome e cognome) si salta e non si aggiorna.
            </li>
            <li>Il foglio «Istruzioni» del tracciato spiega ogni colonna, con un esempio.</li>
          </ul>
          <div className="riga operatori-sezione">
            <a className="bottone bottone-secondario" href="/api/mediatori/importazione/tracciato" download>
              Scarica il tracciato
            </a>
            <Bottone variante="primario" inCorso={inCorso} onClick={() => ingresso.current?.click()}>
              Scegli il foglio compilato
            </Bottone>
            <input ref={ingresso} type="file" hidden
                   accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) void leggi(f) }} />
          </div>
          {inCorso && <Attesa testo="Leggo il foglio e controllo le righe…" />}
        </>
      )}

      {fase === 'anteprima' && anteprima && (
        <>
          <p className="nota-modulo">
            {nomeFile}: {righe.length === 1 ? '1 riga' : `${righe.length} righe`}. Da importare: {pronte}.
            {doppioni > 0 && ` Già nell’albo, saltati: ${doppioni}.`}
            {daCorreggere.length > 0 && ` Da correggere: ${daCorreggere.length}.`}
          </p>
          {anteprima.avvisi?.map((a) => <Avviso key={a} tipo="attenzione">{a}</Avviso>)}
          {!!anteprima.colonne_ignorate?.length && (
            <Avviso tipo="informazione">
              Colonne che non uso: {anteprima.colonne_ignorate.map((c) => `«${c}»`).join(', ')}.
            </Avviso>
          )}
          {daCorreggere.length > 0 && (
            <Avviso tipo="attenzione">
              {daCorreggere.length === 1 ? 'Una riga inclusa ha' : `${daCorreggere.length} righe incluse hanno`} ancora
              qualcosa da correggere: lasciale fuori, oppure correggi il foglio e caricalo di nuovo.
            </Avviso>
          )}
          {ricontrollo && <Attesa testo="Ricontrollo le righe…" />}
          <Tabella intestazioni={['Importa', 'Riga', 'Mediatore', 'Dati', 'Esito']}>
            {righe.map((r) => {
              const fuori = escluse.has(r.numero)
              const l = r.lettura
              return (
                <tr key={r.numero} className={fuori ? 'operatori-riga-esclusa' : undefined}>
                  <td data-etichetta="Importa">
                    <input type="checkbox" checked={!fuori} disabled={ricontrollo || inCorso || r.stato === 'doppione'}
                           aria-label={`Importa la riga ${r.numero}`}
                           onChange={(e) => void cambiaInclusione(r.numero, e.target.checked)} />
                  </td>
                  <td data-etichetta="Riga" className="mono">{r.numero}</td>
                  {/* Un solo figlio per cella: sotto gli 860 px la cella e' una riga flessibile. */}
                  <td data-etichetta="Mediatore"><div>
                    <div className="voce-principale">{r.valori.cognome} {r.valori.nome}</div>
                    <div className="piccolo tenue">{[r.valori.numero_albo && `Albo n. ${r.valori.numero_albo}`, r.valori.email].filter(Boolean).join(' · ')}</div>
                  </div></td>
                  <td data-etichetta="Dati"><div>
                    <div>{l.interno ? 'Interno' : 'Esterno'}{l.lingue.length ? ` · ${l.lingue.join(', ')}` : ''}</div>
                    <div className="piccolo tenue">
                      {[l.comune_lavoro && `Lavora a ${l.comune_lavoro}`, l.tutte_le_sedi ? 'Tutte le sedi' : l.sedi.join(', '),
                        l.comune && `Residenza: ${l.comune}`, l.paese].filter(Boolean).join(' · ')}
                    </div>
                  </div></td>
                  <td data-etichetta="Esito"><div>
                    {r.stato === 'doppione' && <div><Etichetta>Già nell’albo, si salta</Etichetta></div>}
                    {r.stato !== 'doppione' && fuori && <div><Etichetta>Lasciata fuori</Etichetta></div>}
                    {r.stato === 'errore'
                      ? <ul className="operatori-errori-riga">{r.errori.map((x) => <li key={x}>{x}</li>)}</ul>
                      : r.stato === 'nuovo' && !fuori && <Etichetta tono="positivo">Pronta</Etichetta>}
                    {r.avvisi.map((x) => <p key={x} className="piccolo tenue">{x}</p>)}
                  </div></td>
                </tr>
              )
            })}
          </Tabella>
        </>
      )}

      {fase === 'esito' && esito && (
        <>
          <Avviso tipo="successo">
            {esito.creati.length === 1 ? 'Ho inserito 1 mediatore' : `Ho inserito ${esito.creati.length} mediatori`} nell’albo.
            {esito.doppioni.length > 0 && ` Saltati perché già presenti: ${esito.doppioni.length}.`}
          </Avviso>
          {esito.creati.length > 0 && (
            <p className="nota-modulo">
              {esito.creati.map((c) => `${c.cognome} ${c.nome}`).join(', ')}. Per farli entrare nel gestionale usa
              «Dai l’accesso» nella loro riga.
            </p>
          )}
        </>
      )}
    </Modale>
  )
}
